import { defineResource } from '@tnn/core'
import { z } from 'zod'

/**
 * accounting.creditNote (ใบลดหนี้): an invoice's amount lowered after it was issued — a waybill or job re-priced, a
 * discount agreed later — for a customer on account. The invoice stands as issued; the note takes its outstanding down
 * (`credited`). Paid already beyond what it now comes to, the excess becomes the customer's credit: taken off the
 * invoice's payments, it stays with them as an advance (a payment's `unallocated`) for the next invoice (`released`).
 * A note whose credit was released is not cancelled; another invoice bills what was credited by mistake.
 *
 * Freight is exempt from VAT, so this is no tax credit note: the document for the customer of what was taken off.
 */
export const creditNoteStatuses = ['draft', 'issued', 'cancelled'] as const
export type CreditNoteStatus = (typeof creditNoteStatuses)[number]

export const creditNote = defineResource({
  app: 'accounting',
  name: 'creditNote',
  path: 'credit-notes',
  schema: z.object({
    invoiceId: z.string().min(1),
    issueDate: z.iso.date(),
    amount: z.number().positive(),
    reason: z.string().trim().min(1).max(500),
    /** The line it corrects, as the invoice lists it (a waybill, a job), if one */
    line: z.string().trim().max(300).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 140, priority: 1 } },
    invoiceId: {
      type: 'ref',
      ref: { resource: 'accounting.invoice' },
      search: true,
      filter: true,
      list: { width: 150, priority: 1 },
      form: { span: 6 },
    },
    /** The invoice's customer (server-set) */
    customerId: { readOnly: true, type: 'ref', ref: { resource: 'accounting.customer' }, filter: true, list: { priority: 1 } },
    issueDate: { filter: true, list: { width: 130, priority: 2 }, form: { span: 3 } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 3 } },
    reason: { type: 'textarea', list: { priority: 2 }, form: { span: 12 } },
    line: { list: { priority: 3, hidden: true }, form: { span: 12 } },
    /** Of the amount, what was already paid and so went to the customer's credit (server-set) */
    released: { readOnly: true, type: 'money', list: { align: 'end', priority: 3 } },
    status: {
      readOnly: true,
      type: 'select',
      options: creditNoteStatuses,
      filter: true,
      tones: { draft: 'neutral', issued: 'success', cancelled: 'danger' },
      list: { width: 120, priority: 1 },
    },
  },
  title: 'number',
  columns: ['number', 'issueDate', 'customerId', 'invoiceId', 'amount', 'released', 'status', 'reason'],
  defaultSort: '-issueDate',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print'],
  // What was issued is what the customer got: a wrong one is cancelled (while none of it was released) and issued again
  submittable: { cancelReason: 'required', allowOnSubmit: [] },
  card: { title: 'number', subtitle: 'customerId', meta: ['issueDate', 'amount'], status: 'status' },
})
